import React from "react";

import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../context/StateContext";
import useTimezoneDate from "../../../../../hooks/useTimezoneDate";
import { Booking } from "../../../../../models/booking";
import { MilestoneType } from "../../../../../models/milestone";
import { handleGetConvertValue } from "../../../../../util/converts";
import { BookingModes, ShipmentDetails } from "../../../../../util/enums";
import { DecimalFormatType } from "../../../../../util/formatDecimalValue";
import { getWeightMeasurement } from "../../../../../util/getMeasurement";
import { FieldViewProps } from "../../../../Form/FieldView";
import { getDateValueInTimeZone } from "../../../../Generic/MilestoneDates";
import { SectionView } from "../../../../Generic/SideView/SectionView";
import { useBookingResourcesContext } from "../../../hooks/useResourcesContext";
import { getExtendedFormInputInfoByType } from "../../../Maintenance/Details/types";
import { formatPortInputValue } from "../../../util";

type BookingViewDetailsProps = {
  booking: Booking | undefined;
  purchaseOrders: string;
};
export const BookingViewDetails: React.FC<BookingViewDetailsProps> = (props) => {
  const { booking, purchaseOrders } = props;
  const { isFMorSPVersion } = useAppState();
  const { getDateValueUtcTimezone } = useTimezoneDate();
  const measurementWeight = getWeightMeasurement();
  const { t: translate } = useTranslation();
  const { customerList, milestoneDates } = useBookingResourcesContext();

  const baseViewFieldProps = (key: keyof Booking) => {
    const props: FieldViewProps = {
      label: translate(key),
      value: booking?.[key],
    };

    return props;
  };
  const type = MilestoneType.ESTIMATED;
  const milestoneOptions = milestoneDates.filter(
    (milestoneOption) => milestoneOption.type?.toLowerCase() === type.toLowerCase()
  );

  const customerSelected = isFMorSPVersion
    ? booking?.consignee
    : customerList?.find((warehouse) => Number(booking?.consigneeId) === Number(warehouse?.id));

  const milestoneFields = [
    milestoneOptions.map((milestoneOption) => {
      const milestoneValue = booking?.milestones?.find(
        (milestoneValue) => Number(milestoneValue?.milestoneId) === Number(milestoneOption?.id)
      );

      const keyName = milestoneOption.reference || milestoneOption.title;

      return {
        name: keyName,
        label: milestoneOption.title,
        value: milestoneValue?.dateValue
          ? getDateValueInTimeZone(milestoneValue?.dateValue, "dd/MM/yyyy")
          : undefined,
      };
    }),
  ];

  const containerFields: FieldViewProps[][] = (
    booking?.containers?.length ? booking?.containers : [{}]
  )?.map((containerData) => {
    return [
      {
        label: `${translate(ShipmentDetails.CONTAINER)} #`,
        value: containerData?.container,
      },
      {
        label: translate("containerSize"),
        value: containerData?.size,
      },
      {
        label: `${translate(ShipmentDetails.SEAL)} #`,
        value: containerData?.seal,
      },
      {
        label: `${translate("grossWeight")}(${(measurementWeight?.code || "")?.toUpperCase()}) `,
        value: containerData?.grossWeight
          ? handleGetConvertValue(
              Number(containerData?.grossWeight),
              measurementWeight?.code,
              DecimalFormatType.MAERSK
            )
          : undefined,
      },
    ];
  });

  const renderLastRowFormByType = () => {
    if (!booking?.shipmentType) return [];
    switch (booking?.shipmentType.toLowerCase()) {
      case "air":
        return [
          [
            {
              ...baseViewFieldProps(ShipmentDetails.PLANE),
            },
            {
              ...baseViewFieldProps(ShipmentDetails.JOURNEY),
            },
            {
              ...baseViewFieldProps(ShipmentDetails.AIRWAY_BILL),
            },
          ],
        ];
      case "sea":
        return [
          [
            {
              ...baseViewFieldProps(ShipmentDetails.VESSEL_NAME),
              label: translate("vessel"),
            },
            {
              ...baseViewFieldProps(ShipmentDetails.VOYAGE_NUMBER),
              label: translate("voyage"),
            },
            {
              ...baseViewFieldProps(ShipmentDetails.MBL),
              label: `${translate(ShipmentDetails.MBL)} #`,
            },
            {
              ...baseViewFieldProps(ShipmentDetails.HBL),
            },
          ],
        ];
      case "road":
      default:
        return [];
    }
  };

  const renderExtendedPortFormByType = () => {
    if (booking?.shipmentType) {
      const info = getExtendedFormInputInfoByType(booking?.shipmentType, translate);

      if (!info) return [];

      switch (booking?.shipmentType) {
        case BookingModes.AIR:
          return [
            [
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_LOADING_ID),
                label: info.origin?.label,
                value: formatPortInputValue(booking?.portOfLoading),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_DISCHARGE_ID),
                label: info.discharge?.label,
                value: formatPortInputValue(booking?.portOfDischarge),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_RECEIPT_ID),
                label: info.receipt?.label,
                value: formatPortInputValue(booking?.portOfReceipt),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_DESTINATION_ID),
                label: info.destination?.label,
                value: formatPortInputValue(booking?.portOfDestination),
              },
            ],
          ];
        case BookingModes.SEA:
          return [
            [
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_LOADING_ID),
                label: info.origin?.label,
                value: formatPortInputValue(booking?.portOfLoading),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_DISCHARGE_ID),
                label: info.discharge?.label,
                value: formatPortInputValue(booking?.portOfDischarge),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_RECEIPT_ID),
                label: info.receipt?.label,
                value: formatPortInputValue(booking?.portOfReceipt),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.PORT_OF_DESTINATION_ID),
                label: info.destination.label,
                value: formatPortInputValue(booking?.portOfDestination),
              },
            ],
          ];
        case BookingModes.ROAD:
        default:
          return [
            [
              {
                ...baseViewFieldProps(ShipmentDetails.ORIGIN),
                label: info.origin?.label,
                value: formatPortInputValue(booking?.portOfLoading),
              },
              {
                ...baseViewFieldProps(ShipmentDetails.DESTINATION),
                label: info.destination?.label,
                value: formatPortInputValue(booking?.portOfDestination),
              },
            ],
          ];
      }
    }

    return [];
  };

  const generalFields = [
    [
      {
        ...baseViewFieldProps(ShipmentDetails.CONTAINER_ID),
        label: `${translate("shipmentNumber")} #`,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.CARGO_READY_DATE),
        value: booking?.[ShipmentDetails.CARGO_READY_DATE] ? format(
          new Date(String(getDateValueUtcTimezone(booking?.[ShipmentDetails.CARGO_READY_DATE]))),
          "dd/MM/yyyy"
        ) : "",
      },
      {
        ...baseViewFieldProps(ShipmentDetails.ASN),
        label: `${translate(ShipmentDetails.ASN)} #`,
      },
    ],
    ...containerFields,
  ];

  const consigneeFields = [
    [
      {
        label: translate("consignee"),
        value: customerSelected?.fullName,
      },
      {
        label: translate(ShipmentDetails.CONSIGNEE_CODE),
        value: customerSelected?.code,
      },
    ],
    [
      {
        ...baseViewFieldProps(ShipmentDetails.SERVICE_TYPE),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.BOUND),
        label: translate("shipment"),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.DELIVER_TO),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.CARRIER),
      },
    ],
  ];

  const shipmentFields = [
    [
      {
        ...baseViewFieldProps(ShipmentDetails.SHIPMENT_TYPE),
      },
      {
        label: translate(ShipmentDetails.PURCHASE_ORDERS),
        value: purchaseOrders,
      },
      {
        ...baseViewFieldProps(ShipmentDetails.SHIP_TERMS),
      },
    ],
    ...renderLastRowFormByType(),
    [
      {
        ...baseViewFieldProps(ShipmentDetails.OFFICE_ID),
        label: translate("office"),
      },
      {
        ...baseViewFieldProps(ShipmentDetails.NOTIFY_PARTY_ID),
        label: translate("notifyParty"),
      },
    ],
    ...renderExtendedPortFormByType(),
  ];

  return (
    <div>
      <SectionView fields={generalFields} title={translate("general")} />
      <SectionView fields={consigneeFields} title={translate("consignee")} />
      <SectionView fields={shipmentFields} title={translate("shipment")} />
      <SectionView fields={milestoneFields} title={translate("estimatedDates")} />
    </div>
  );
};
